import * as PopoverPrimitive from '@radix-ui/react-popover';
import { ComponentPropsWithoutRef, ElementRef, forwardRef } from 'react';
import { useState } from 'react';
import { Separator } from 'src/shared/components/ui/separator';
import { cn } from 'src/shared/lib/utils';

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = forwardRef<
  ElementRef<typeof PopoverPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>((props, ref) => {
  const { className, ...rest } = props;
  const [side, setSide] = useState<string | undefined>();

  function getSide() {
    setTimeout(() => {
      const content = document.querySelector('[data-radix-popper-content-wrapper]');
      if (content) {
        const contentChildren = content.children;
        if (contentChildren.length > 0) {
          const side = contentChildren[0].getAttribute('data-side') || undefined;
          setSide(side);
        }
      }
    }, 100);
  }

  return (
    <PopoverPrimitive.Anchor asChild>
      <PopoverPrimitive.Trigger ref={ref} data-side={side} className={cn(className)} onClick={getSide} {...rest} />
    </PopoverPrimitive.Anchor>
  );
});
PopoverTrigger.displayName = PopoverPrimitive.Trigger.displayName;

const PopoverContent = forwardRef<
  ElementRef<typeof PopoverPrimitive.Content>,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    animate?: boolean;
  }
>(({ className, align = 'center', sideOffset = 4, animate = true, ...props }, ref) => (
  <PopoverPrimitive.Content
    ref={ref}
    align={align}
    sideOffset={sideOffset}
    className={cn(
      'z-50 rounded-sm border bg-white p-4 text-typography-black shadow-md outline-none',
      {
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2':
          animate,
      },
      className
    )}
    {...props}
  />
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

type PopoverNavigationProps = {
  categories: Record<string, string> | string[];
  selected: string;
  setSelected: (selected: string) => void;
};

function PopoverNavigation({ categories, selected, setSelected }: PopoverNavigationProps) {
  const iterator = Array.isArray(categories)
    ? categories.map((category) => [category, category])
    : Object.entries(categories);

  return (
    <>
      <div className="flex gap-2 pt-1 px-1 bg-white">
        {iterator.map(([key, display]) => (
          <button
            key={key}
            onClick={() => setSelected(key)}
            className={cn(
              'z-10 rounded-t-sm transition-all px-2 h-7 hover:bg-neutral-200/50 hover:text-typography-black text-typography-grey',
              selected === key && 'text-typography-black border-b border-black'
            )}
          >
            {display}
          </button>
        ))}
      </div>
      <Separator className="-translate-y-[1px]" />
    </>
  );
}
export { Popover, PopoverContent, PopoverTrigger, PopoverNavigation };
